import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  Divider,
  Collapse,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Sync as SyncIcon,
  Pages as PagesIcon,
  FiberManualRecord as DotIcon,
  MoreVert as MoreVertIcon,
  ArrowForward as ArrowForwardIcon,
  ChatBubbleOutline as CommentsIcon,
  MailOutline as MessagesIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';
import Messages from '../instagram/Messages';
import Comments from '../instagram/Comments';

const FB_BLUE = '#1877F2';
const IG_PINK = '#E4405F';

const fbContainedSx = {
  bgcolor: FB_BLUE,
  '&:hover': { bgcolor: '#166fe5' },
};

const isPageConnected = (page) => Boolean(page?.isConnected ?? page?.is_connected);

const formatConnectedDate = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getPageProfilePicture = (page) =>
  page?.pageProfilePicture || page?.page_profile_picture || null;

const getInstagramAccounts = (page) => {
  if (!page?.instagramConnected) return [];
  return [{
    id: page.instagramBusinessAccountId,
    username: page.instagramUsername,
    profilePicture: page.instagramProfilePicture || page.instagram_profile_picture,
    connectedAt: page.instagramConnectedAt,
  }];
};

const getPageInstagramAccount = (pageId, connectedAccounts = []) =>
  connectedAccounts.find((account) => account.pageId === pageId) || null;

const inboxKey = (pageId, accountId) => `${String(pageId)}:${String(accountId)}`;

const PageAvatar = ({ page, size = 40 }) => {
  const src = getPageProfilePicture(page);
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={page?.name || 'Facebook Page'}
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          objectFit: 'cover',
          flexShrink: 0,
          bgcolor: 'grey.100',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        bgcolor: FB_BLUE,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontWeight: 600,
        fontSize: size * 0.38,
      }}
    >
      {(page?.name || 'P').charAt(0).toUpperCase()}
    </Box>
  );
};

const InstagramAvatar = ({ account, size = 36 }) => {
  const src = account?.profilePicture;
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={account?.username ? `@${account.username}` : 'Instagram'}
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          bgcolor: 'grey.100',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <InstagramIcon sx={{ color: IG_PINK, fontSize: size * 0.55 }} />
    </Box>
  );
};

const FacebookIntegration = () => {
  const { pageId } = useParams();
  const [searchParams] = useSearchParams(); // kept for future query param usage
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal, openErrorModal } = useModal();

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [expandedInboxKey, setExpandedInboxKey] = useState(null);
  const [collapsedPageIds, setCollapsedPageIds] = useState(() => new Set());
  const [instagramStatus, setInstagramStatus] = useState(null);
  const [pageMenuAnchor, setPageMenuAnchor] = useState(null);
  const [pageMenuTarget, setPageMenuTarget] = useState(null);
  const [igMenuAnchor, setIgMenuAnchor] = useState(null);
  const [igMenuTarget, setIgMenuTarget] = useState(null);
  // 'messages' | 'comments' per inbox key
  const [inboxTab, setInboxTab] = useState({});
  const userToggledInboxRef = useRef(false);
  const initialExpandDoneRef = useRef(false);
  const initialPageCollapseDoneRef = useRef(false);

  const loadPages = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      const response = await api.get('/lms/integrations/facebook/pages/');
      setPages(response.data.pages || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error loading Facebook pages:', error);
      }
      setPages([]);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  useEffect(() => {
    const loadInstagramStatus = async () => {
      try {
        const response = await api.get('/lms/integrations/instagram/');
        setInstagramStatus(response.data);
      } catch {
        setInstagramStatus(null);
      }
    };
    loadInstagramStatus();
  }, [pages]);

  const connectedPages = pages.filter(isPageConnected);

  useEffect(() => {
    userToggledInboxRef.current = false;
    initialExpandDoneRef.current = false;
    initialPageCollapseDoneRef.current = false;
  }, [pageId]);

  useEffect(() => {
    if (initialPageCollapseDoneRef.current || connectedPages.length === 0) return;

    const keepExpandedId = pageId
      ? String(pageId)
      : String(connectedPages[0]?.id || '');

    const collapsed = new Set(
      connectedPages
        .filter((p) => String(p.id) !== keepExpandedId)
        .map((p) => String(p.id)),
    );
    setCollapsedPageIds(collapsed);
    initialPageCollapseDoneRef.current = true;
  }, [connectedPages, pageId]);

  useEffect(() => {
    if (userToggledInboxRef.current || initialExpandDoneRef.current) return;
    if (connectedPages.length === 0) return;

    let key = null;
    if (pageId) {
      const page = connectedPages.find((p) => String(p.id) === String(pageId));
      if (page?.instagramConnected && page.instagramBusinessAccountId) {
        key = inboxKey(page.id, page.instagramBusinessAccountId);
      }
    }

    if (key) {
      setExpandedInboxKey(key);
      initialExpandDoneRef.current = true;
    }
  }, [pageId, connectedPages]);

  const toggleInbox = (pageIdArg, accountId) => {
    userToggledInboxRef.current = true;
    const key = inboxKey(pageIdArg, accountId);
    setExpandedInboxKey((prev) => (prev === key ? null : key));
  };

  const isInboxExpanded = (pid, accountId) => expandedInboxKey === inboxKey(pid, accountId);
  const isPageExpanded = (id) => !collapsedPageIds.has(String(id));

  const togglePage = (id) => {
    const key = String(id);
    setCollapsedPageIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openPageMenu = (event, page) => {
    event.stopPropagation();
    setPageMenuAnchor(event.currentTarget);
    setPageMenuTarget(page);
  };

  const closePageMenu = () => {
    setPageMenuAnchor(null);
    setPageMenuTarget(null);
  };

  const openIgMenu = (event, page) => {
    event.stopPropagation();
    setIgMenuAnchor(event.currentTarget);
    setIgMenuTarget(page);
  };

  const closeIgMenu = () => {
    setIgMenuAnchor(null);
    setIgMenuTarget(null);
  };

  const handleConnectFacebook = async () => {
    try {
      showLoader('Preparing Facebook connection...');
      const response = await api.get('/lms/integrations/facebook/oauth/start/', { params: { mode: 'full' } });
      const { authorization_url: authorizationUrl } = response.data;
      if (!authorizationUrl) throw new Error('No authorization URL received');

      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      const popup = window.open(
        authorizationUrl,
        'Facebook OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`,
      );
      hideLoader();
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        openErrorModal('Popup was blocked. Please allow popups for this site and try again.');
        return;
      }
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          loadPages(false);
        }
      }, 500);
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to start Facebook connection.');
    }
  };

  const openAddPageDialog = async () => {
    try {
      showLoader('Loading pages...');
      const response = await api.get('/lms/integrations/facebook/pages/');
      hideLoader();
      const allPages = response.data.pages || [];
      const disconnected = allPages.filter((p) => !isPageConnected(p));
      if (disconnected.length === 0) {
        openErrorModal('No additional pages available. Reconnect OAuth to grant access to more Pages.');
        return;
      }
      setAvailablePages(disconnected);
      setConnectDialogOpen(true);
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to load Facebook pages.');
    }
  };

  const handleConnectPage = async (id) => {
    try {
      showLoader('Connecting page...');
      await api.post('/lms/integrations/facebook/pages/connect/', { page_id: id });
      hideLoader();
      openSuccessModal('Facebook page connected.');
      setConnectDialogOpen(false);
      await loadPages(false);
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to connect page.');
    }
  };

  const handleDiscoverInstagram = async (id) => {
    try {
      showLoader('Linking Instagram...');
      const response = await api.post('/lms/integrations/facebook/pages/discover-instagram/', { page_id: id });
      hideLoader();
      const instagram = response.data.instagram || {};
      if (instagram.connected) {
        openSuccessModal(`@${instagram.instagram_username || 'account'} linked successfully.`);
        const igId = instagram.instagram_business_id || instagram.instagramBusinessAccountId;
        if (igId) {
          userToggledInboxRef.current = true;
          setExpandedInboxKey(inboxKey(id, igId));
        }
        setCollapsedPageIds((prev) => {
          const next = new Set(prev);
          next.delete(String(id));
          return next;
        });
      } else {
        openErrorModal(instagram.discovery_error || 'Instagram could not be linked.');
      }
      await loadPages(false);
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to link Instagram.');
    }
  };

  const handleDisconnectInstagram = async (id) => {
    try {
      showLoader('Disconnecting Instagram...');
      await api.post('/lms/integrations/facebook/pages/disconnect-instagram/', { page_id: id });
      hideLoader();
      openSuccessModal('Instagram disconnected.');
      setExpandedInboxKey((prev) => (prev?.startsWith(`${id}:`) ? null : prev));
      await loadPages(false);
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to disconnect Instagram.');
    }
  };

  const handleDisconnectPage = async (id) => {
    try {
      showLoader('Disconnecting page...');
      await api.post('/lms/integrations/facebook/pages/disconnect/', { page_id: id });
      hideLoader();
      openSuccessModal('Facebook page disconnected.');
      setExpandedInboxKey((prev) => (prev?.startsWith(`${id}:`) ? null : prev));
      await loadPages(false);
    } catch (error) {
      hideLoader();
      openErrorModal(error.response?.data?.error || 'Failed to disconnect page.');
    }
  };

  const renderPageHeaderActions = (page) => (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexShrink: 0 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<LinkIcon fontSize="small" />}
        onClick={() => handleDiscoverInstagram(page.id)}
      >
        Add Instagram Account
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<SyncIcon fontSize="small" />}
        onClick={handleConnectFacebook}
      >
        Reconnect
      </Button>
      <IconButton size="small" onClick={(e) => openPageMenu(e, page)} aria-label="Page options">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => togglePage(page.id)}
        aria-label={isPageExpanded(page.id) ? 'Collapse page' : 'Expand page'}
      >
        {isPageExpanded(page.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Box>
  );

  const renderInstagramInbox = (page, account) => {
    const connectedAccounts = instagramStatus?.connectedAccounts || [];
    const pageAccount = getPageInstagramAccount(page.id, connectedAccounts) || (page.instagramConnected ? {
      pageId: page.id,
      pageName: page.name,
      instagramConnected: true,
      instagramUsername: page.instagramUsername,
      instagramBusinessId: page.instagramBusinessAccountId,
      instagramProfilePicture: page.instagramProfilePicture,
    } : null);
    if (!pageAccount) return null;

    const key = inboxKey(page.id, account.id);
    const activeTab = inboxTab[key] || 'messages';

    return (
      <Collapse in={isInboxExpanded(page.id, account.id)}>
        <Box sx={{ px: 2, pb: 2, pt: 0, bgcolor: 'background.paper' }}>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {/* Tab header */}
            <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
              {[
                { id: 'messages', label: 'Messages', icon: <MessagesIcon sx={{ fontSize: 15 }} /> },
                { id: 'comments', label: 'Comments', icon: <CommentsIcon sx={{ fontSize: 15 }} /> },
              ].map((t) => (
                <Box
                  key={t.id}
                  onClick={() => setInboxTab((prev) => ({ ...prev, [key]: t.id }))}
                  sx={{
                    px: 2, py: 1.25,
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    cursor: 'pointer',
                    fontWeight: activeTab === t.id ? 700 : 400,
                    fontSize: 13,
                    color: activeTab === t.id ? 'primary.main' : 'text.secondary',
                    borderBottom: 2,
                    borderColor: activeTab === t.id ? 'primary.main' : 'transparent',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {t.icon}
                  {t.label}
                </Box>
              ))}
            </Box>

            {activeTab === 'messages' ? (
              <Messages
                connectedAccounts={[pageAccount]}
                pageId={page.id}
                embedded
                allowReply={false}
              />
            ) : (
              <Comments
                connectedAccounts={[pageAccount]}
                pageId={page.id}
                embedded
                allowReply={false}
              />
            )}
          </Box>
        </Box>
      </Collapse>
    );
  };

  const renderInstagramRow = (page, account, isLast) => {
    const managing = isInboxExpanded(page.id, account.id);

    return (
      <Box key={account.id}>
        <Box
          sx={{
            px: 2,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            bgcolor: managing ? 'action.hover' : 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 200 }}>
            <InstagramAvatar account={account} size={36} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                @{account.username || 'instagram'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {account.id || '—'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 100 }}>
            <DotIcon sx={{ fontSize: 8, color: 'success.main' }} />
            <Typography variant="caption" color="success.main" fontWeight={500}>Connected</Typography>
          </Box>

          <Box sx={{ minWidth: 160, display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="caption" color="text.secondary" display="block">Connected on</Typography>
            <Typography variant="body2">{formatConnectedDate(account.connectedAt)}</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => toggleInbox(page.id, account.id)}
            >
              Manage
            </Button>
            <IconButton size="small" onClick={(e) => openIgMenu(e, page)} aria-label="Instagram options">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {!isLast && <Divider />}
        {renderInstagramInbox(page, account)}
      </Box>
    );
  };

  const renderPageCard = (page) => {
    const accounts = getInstagramAccounts(page);
    const pageExpanded = isPageExpanded(page.id);

    return (
      <Card key={page.id} variant="outlined">
        <Box
          sx={{
            px: 2,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          <PageAvatar page={page} />
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
              <Typography variant="subtitle1" fontWeight={600}>{page.name}</Typography>
              <Chip label="Active" size="small" color="success" variant="outlined" />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Page ID: {page.id}
            </Typography>
          </Box>
          {renderPageHeaderActions(page)}
        </Box>

        <Collapse in={pageExpanded}>
          <Divider />
          <Box sx={{ px: 2, py: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Connected Instagram Accounts
            </Typography>

            {accounts.length === 0 ? (
              <Card variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {page.instagramDiscoveryError || 'No Instagram account linked to this Page yet.'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon fontSize="small" />}
                  onClick={() => handleDiscoverInstagram(page.id)}
                >
                  Link Instagram Account
                </Button>
              </Card>
            ) : (
              <Card variant="outlined" sx={{ bgcolor: 'background.paper', overflow: 'hidden' }}>
                {accounts.map((account, index) => renderInstagramRow(
                  page,
                  account,
                  index === accounts.length - 1,
                ))}
                <Divider />
                <Box sx={{ px: 2, py: 1.25 }}>
                  <Button
                    size="small"
                    variant="createBtnText"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={() => handleDiscoverInstagram(page.id)}
                  >
                    Link New Instagram Account
                  </Button>
                </Box>
              </Card>
            )}
          </Box>
        </Collapse>
      </Card>
    );
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={() => loadPages()}
        >
          Refresh
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<AddIcon fontSize="small" />}
          onClick={connectedPages.length > 0 ? openAddPageDialog : handleConnectFacebook}
        >
          Add Page
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          px: 2,
          py: 1.75,
          mb: 2,
          borderRadius: 1,
          bgcolor: 'rgba(0, 112, 80, 0.06)',
          border: 1,
          borderColor: 'rgba(0, 112, 80, 0.12)',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            Manage your Facebook Pages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connect Facebook Pages for Lead Ads. Instagram Business accounts linked to your Pages
            can be managed below.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <FacebookIcon sx={{ color: FB_BLUE, fontSize: 28 }} />
          <ArrowForwardIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
          <InstagramIcon sx={{ color: IG_PINK, fontSize: 28 }} />
        </Box>
      </Box>

      {connectedPages.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <FacebookIcon sx={{ fontSize: 48, color: FB_BLUE, mb: 1 }} />
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            No Facebook Pages connected
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect a Page to receive Lead Ads and manage linked Instagram accounts.
          </Typography>
          <Button
            variant="contained"
            startIcon={<FacebookIcon />}
            onClick={handleConnectFacebook}
            sx={fbContainedSx}
          >
            Connect Page
          </Button>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {connectedPages.map(renderPageCard)}
        </Stack>
      )}
        </>
      )}

      <Menu anchorEl={pageMenuAnchor} open={Boolean(pageMenuAnchor)} onClose={closePageMenu}>
        <MenuItem
          onClick={() => {
            if (pageMenuTarget) handleDiscoverInstagram(pageMenuTarget.id);
            closePageMenu();
          }}
        >
          <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Refresh Instagram</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (pageMenuTarget) handleDisconnectPage(pageMenuTarget.id);
            closePageMenu();
          }}
        >
          <ListItemIcon><LinkOffIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Disconnect Page</ListItemText>
        </MenuItem>
      </Menu>

      <Menu anchorEl={igMenuAnchor} open={Boolean(igMenuAnchor)} onClose={closeIgMenu}>
        <MenuItem
          onClick={() => {
            if (igMenuTarget) handleDiscoverInstagram(igMenuTarget.id);
            closeIgMenu();
          }}
        >
          <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Refresh</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleConnectFacebook();
            closeIgMenu();
          }}
        >
          <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Reconnect</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (igMenuTarget) handleDisconnectInstagram(igMenuTarget.id);
            closeIgMenu();
          }}
        >
          <ListItemIcon><LinkOffIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Disconnect Instagram</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={connectDialogOpen} onClose={() => setConnectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PagesIcon sx={{ color: FB_BLUE }} />
            <Typography variant="h6" fontWeight={600}>Add Facebook Page</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a Page to connect to SalesGro.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            {availablePages.map((page) => (
              <Card
                key={page.id}
                variant="outlined"
                sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <PageAvatar page={page} size={36} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{page.name}</Typography>
                    <Typography variant="caption" color="text.secondary">Page ID: {page.id}</Typography>
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<LinkIcon fontSize="small" />}
                  onClick={() => handleConnectPage(page.id)}
                  sx={fbContainedSx}
                >
                  Connect
                </Button>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacebookIntegration;
